import { getExchangeRate } from "@/common/utilities/math/asConvertedCurrency";

/**
 * Price-filter sliders are anchored in EUR and scaled into whatever currency
 * the visitor is browsing in.
 *
 * These used to be hand-typed tables covering fourteen currencies, which meant
 * a location priced in anything else produced `undefined` bounds -- and every
 * comparison against `undefined` is false, so the slider silently stopped
 * filtering. Deriving them from the live rate means a new market works the day
 * its first location is listed.
 */
export interface FilterBounds {
    max: number;
    step: number;
}

/** Bookable resources are priced per hour. */
export const HOURLY_EUR_BOUNDS: FilterBounds = { max: 150, step: 10 };

/**
 * Private offices are monthly leases and run far higher than an hourly booking,
 * so they carry their own anchors: up to EUR 20,000 for a whole office, and up
 * to EUR 750 per seat (per-seat rents cluster well below the office total).
 */
export const OFFICE_TOTAL_EUR_BOUNDS: FilterBounds = { max: 20000, step: 500 };
export const OFFICE_SEAT_EUR_BOUNDS: FilterBounds = { max: 750, step: 50 };

/**
 * Rounds a raw converted figure to something a slider can sensibly step
 * through: one or two significant digits, so EUR 150 becomes "600" in a
 * currency trading at 4:1 rather than "612.47".
 *
 * Exported for `budgetBounds`, which scales its own anchor the same way -- the
 * lead form's budget slider and the price filters should round a converted
 * figure identically rather than each inventing a house style.
 */
export const toRoundNumber = (value: number): number => {
    if (!Number.isFinite(value) || value <= 0) return 0;
    const magnitude = 10 ** Math.floor(Math.log10(value));
    // Below 10 in the target currency (a zero-decimal currency is never this
    // small, so this only catches genuinely tiny anchors) keep whole units.
    if (magnitude < 1) return Math.max(1, Math.round(value));
    const scaled = value / magnitude;
    const rounded = scaled < 1.5 ? 1 : scaled < 3.5 ? 2 : scaled < 7.5 ? 5 : 10;
    return rounded * magnitude;
};

/**
 * Scales an EUR anchor into `currency`.
 *
 * The step is rounded first and the max is then snapped to a whole number of
 * steps, so the slider's top value is always reachable -- an off-step maximum
 * leaves a range the user can never actually select.
 */
export const scaleBounds = (
    anchor: FilterBounds,
    rate: number
): FilterBounds => {
    // A step below one major unit is not useful on any of these sliders -- even
    // in a currency with sub-units, nobody filters offices by the cent.
    const step = Math.max(1, toRoundNumber(anchor.step * rate));
    const max = Math.max(step, Math.round((anchor.max * rate) / step) * step);
    return { max, step };
};

/**
 * The bounds for `currency`, scaled from the EUR anchor at the current rate.
 *
 * Falls back to the unscaled EUR anchor when no rate is available. That is
 * wrong in magnitude but bounded and usable, which beats `undefined` bounds
 * that make the filter quietly match everything.
 */
export const boundsForCurrency = async (
    anchor: FilterBounds,
    currency: string
): Promise<FilterBounds> => {
    if (currency === "EUR") return anchor;
    const rate = await getExchangeRate("EUR", currency);
    if (rate === null) return anchor;
    return scaleBounds(anchor, rate);
};

/**
 * The part of a slider's range that actually constrains anything.
 *
 * A slider always has a position, but a filter only belongs in the URL once it
 * excludes something: a thumb resting against its end of the track is the
 * absence of a limit, not a limit of its own. Writing those resting positions
 * out anyway is how a visitor who never touched the filter ends up with one --
 * and, because the bounds are scaled per currency and resolved asynchronously,
 * how they ended up with the *EUR* anchor as a limit in their own currency:
 * "up to 150" is the whole range in euros and a sixth of it in Danish kroner.
 *
 * Returning null for a resting thumb also keeps the filter out of the active
 * count, so the mobile Filters button does not claim a filter nobody set.
 */
export const constrainingRange = (
    range: { minimum: number; maximum: number },
    bounds: { min: number; max: number }
): { minimum: number | null; maximum: number | null } => ({
    minimum: range.minimum > bounds.min ? range.minimum : null,
    maximum: range.maximum < bounds.max ? range.maximum : null,
});
